import { Timestamp } from 'firebase/firestore';

/**
 * Calculates a pet's estimated birthday based on their age
 * @param age - Pet's age in years (can be decimal like 3.5)
 * @param currentDate - Current date (defaults to today)
 * @returns Timestamp representing the estimated birthday
 */
export const calculateBirthdayFromAge = (
  age: number,
  currentDate: Date = new Date()
): Timestamp => {
  // Calculate the estimated birthday
  // We'll use the same day of the month as today for consistency
  const currentDay = currentDate.getDate();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Calculate the birth year
  const birthYear = currentYear - Math.floor(age);
  
  // Calculate the birth month and day
  // For fractional ages, we'll estimate the month based on the decimal part
  const ageDecimal = age - Math.floor(age);
  let birthMonth = currentMonth;
  let birthDay = currentDay;
  
  if (ageDecimal > 0) {
    // Estimate the month based on the decimal part of the age
    // 0.5 years = 6 months, 0.25 years = 3 months, etc.
    const monthsToSubtract = Math.round(ageDecimal * 12);
    birthMonth = currentMonth - monthsToSubtract;
    
    // Handle year rollover
    if (birthMonth < 0) {
      birthMonth += 12;
      // Adjust the year when month rolls over
      const adjustedBirthYear = birthYear - 1;
      return Timestamp.fromDate(new Date(adjustedBirthYear, birthMonth, birthDay));
    }
  }
  
  // Create the birthday date
  const birthdayDate = new Date(birthYear, birthMonth, birthDay);
  
  // Convert to Firestore Timestamp
  return Timestamp.fromDate(birthdayDate);
};

/**
 * Calculates a pet's current age based on their birthday
 * @param birthday - Pet's birthday as Timestamp, Date, or string
 * @param currentDate - Current date (defaults to today)
 * @returns Age in years (decimal)
 */
export const calculateAgeFromBirthday = (
  birthday: Timestamp | Date | string,
  currentDate: Date = new Date()
): number => {
  let birthDate: Date;
  
  if (birthday instanceof Date) {
    birthDate = birthday;
  } else if (typeof birthday === 'string') {
    birthDate = new Date(birthday);
  } else if (birthday && typeof birthday === 'object' && 'toDate' in birthday) {
    // Firestore Timestamp
    birthDate = birthday.toDate();
  } else {
    console.warn('Invalid birthday format for age calculation:', birthday);
    return 0;
  }
  const current = currentDate;

  // Calculate the difference in years
  let age = current.getFullYear() - birthDate.getFullYear();

  // Adjust if birthday hasn't occurred this year
  const monthDiff = current.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && current.getDate() < birthDate.getDate())) {
    age--;
  }

  // Calculate the last birthday date
  const lastBirthday = new Date(current.getFullYear(), birthDate.getMonth(), birthDate.getDate());

  // If the last birthday hasn't occurred this year, adjust
  if (lastBirthday > current) {
    lastBirthday.setFullYear(current.getFullYear() - 1);
  }

  // Calculate days since last birthday and days in the current year
  const daysSinceLastBirthday = Math.floor((current.getTime() - lastBirthday.getTime()) / (1000 * 60 * 60 * 24));

  // Determine if current year is a leap year to calculate accurate year length
  const isLeapYear = (lastBirthday.getFullYear() % 4 === 0 && lastBirthday.getFullYear() % 100 !== 0) ||
                     (lastBirthday.getFullYear() % 400 === 0);
  const daysInYear = isLeapYear ? 366 : 365;

  // Calculate the decimal part of the age
  const decimalPart = daysSinceLastBirthday / daysInYear;

  return age + decimalPart;
};

/**
 * Formats a birthday for display
 * @param birthday - Pet's birthday as Timestamp, Date, or string
 * @param userLanguage - User's preferred language
 * @returns Formatted birthday string
 */
export const formatBirthday = (
  birthday: Timestamp | Date | string,
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en'
): string => {
  console.log('🔍 formatBirthday called with:', { birthday, userLanguage, type: typeof birthday });
  
  let date: Date;
  
  if (birthday instanceof Date) {
    date = birthday;
    console.log('📅 Using Date object:', date);
  } else if (typeof birthday === 'string') {
    // Check if it's already a formatted date string in the correct language
    if (userLanguage === 'zh-CN' && birthday.match(/\d{4}年\d{1,2}月\d{1,2}日/)) {
      console.log('📅 Already formatted Chinese date, returning as-is:', birthday);
      return birthday;
    } else if ((userLanguage === 'es' || userLanguage === 'fr') && birthday.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      console.log('📅 Already formatted Spanish/French date, returning as-is:', birthday);
      return birthday;
    } else if (userLanguage === 'en' && birthday.match(/\d{1,2}\/\d{1,2}\/\d{4}/)) {
      console.log('📅 Already formatted English date, returning as-is:', birthday);
      return birthday;
    } else {
      // Try to parse as a regular date string
      date = new Date(birthday);
      console.log('📅 Parsed string to Date:', date);
    }
  } else if (birthday && typeof birthday === 'object' && 'toDate' in birthday) {
    // Firestore Timestamp
    date = birthday.toDate();
    console.log('📅 Converted Timestamp to Date:', date);
  } else {
    console.warn('❌ Invalid birthday format:', birthday);
    return 'Invalid date';
  }
  
  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn('❌ Invalid date object:', date);
    return 'Invalid date';
  }
  
  const month = date.getMonth() + 1; // JavaScript months are 0-based
  const day = date.getDate();
  const year = date.getFullYear();
  
  console.log('📅 Date components:', { month, day, year });
  
  const formats = {
    en: `${month}/${day}/${year}`,
    'zh-CN': `${year}年${month}月${day}日`,
    es: `${day}/${month}/${year}`,
    fr: `${day}/${month}/${year}`
  };
  
  const result = formats[userLanguage] || formats.en;
  console.log('📅 Formatted result:', result);
  
  return result;
};

/**
 * Gets the next birthday for a pet
 * @param birthday - Pet's birthday as Timestamp
 * @param currentDate - Current date (defaults to today)
 * @returns Timestamp of the next birthday
 */
export const getNextBirthday = (
  birthday: Timestamp,
  currentDate: Date = new Date()
): Timestamp => {
  const birthDate = birthday.toDate();
  const current = currentDate;
  
  // Get this year's birthday
  const thisYearBirthday = new Date(current.getFullYear(), birthDate.getMonth(), birthDate.getDate());
  
  // If this year's birthday has passed, get next year's
  const nextBirthday = thisYearBirthday <= current 
    ? new Date(current.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate())
    : thisYearBirthday;
  
  return Timestamp.fromDate(nextBirthday);
};

/**
 * Parses a date string into a Date object based on user language
 * @param dateString - Date string in language-specific format
 * @param userLanguage - User's preferred language
 * @returns Date object or null if parsing fails
 */
export const parseDateFromString = (
  dateString: string,
  userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'en'
): Date | null => {
  console.log('🔍 parseDateFromString called with:', { dateString, userLanguage });
  
  if (!dateString || typeof dateString !== 'string') {
    console.log('❌ Invalid dateString:', dateString);
    return null;
  }

  try {
    switch (userLanguage) {
      case 'zh-CN':
        // Format: 2024年1月15日
        const zhMatch = dateString.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
        if (zhMatch) {
          const [, year, month, day] = zhMatch;
          const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          console.log('📅 Parsed Chinese date:', { year, month, day, parsedDate });
          return parsedDate;
        }
        break;

      case 'es':
      case 'fr':
        // Format: DD/MM/YYYY
        const slashMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (slashMatch) {
          const [, day, month, year] = slashMatch;
          const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          console.log('📅 Parsed Spanish/French date:', { day, month, year, parsedDate });
          return parsedDate;
        }
        break;

      case 'en':
      default:
        // Format: MM/DD/YYYY
        const enMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (enMatch) {
          const [, month, day, year] = enMatch;
          const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          console.log('📅 Parsed English date:', { month, day, year, parsedDate });
          return parsedDate;
        }
        break;
    }
  } catch (error) {
    console.warn('❌ Failed to parse date string:', dateString, error);
  }

  console.log('❌ No valid date pattern matched for:', dateString);
  return null;
};

/**
 * Calculates days until next birthday
 * @param birthday - Pet's birthday as Timestamp
 * @param currentDate - Current date (defaults to today)
 * @returns Number of days until next birthday
 */
export const getDaysUntilBirthday = (
  birthday: Timestamp,
  currentDate: Date = new Date()
): number => {
  const nextBirthday = getNextBirthday(birthday, currentDate);
  const nextBirthdayDate = nextBirthday.toDate();

  const timeDiff = nextBirthdayDate.getTime() - currentDate.getTime();
  return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
};
